import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Clock3,
  FileWarning,
  Percent,
  Receipt,
  Target,
} from "lucide-react";
import { getFinanceCollectionsReport } from "../../../api/reports/finance/financeReports";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import FinanceReportFilter from "./FinanceReportFilter";
import { CollectionsCharts } from "./FinanceReportCharts";
import { exportCollectionsExcel } from "./exportFinanceReportExcel";
import { exportCollectionsPdf } from "./exportFinanceReportPdf";
import {
  Badge,
  DaysOutstandingBadge,
  EmptyState,
  InvoiceStatusBadge,
  MobileReportCard,
  MobileReportCardList,
  PayerTypeBadge,
  ReportPageHeader,
  SectionCard,
  SummaryStatCard,
  SummaryStatGrid,
  dateVN,
  money,
} from "./FinanceReportUi";
import {
  calcCollectionsKpis,
  currentPeriodDefaults,
  periodLabel,
} from "./financePeriodUtils";
import {
  ReportTablePagination,
  useReportTablePage,
} from "./useReportTablePage";

export default function FinanceCollectionsReportPage() {
  const [filters, setFilters] = useState(() => ({
    ...currentPeriodDefaults(),
    payer_type: "",
  }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        from_date: filters.from_date,
        to_date: filters.to_date,
      };
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (filters.payer_type) params.payer_type = filters.payer_type;
      if (filters.direction) params.direction = filters.direction;
      if (filters.group) params.group = filters.group;
      const res = await getFinanceCollectionsReport(params);
      setData(res);
    } catch (err) {
      showErrorToast(err?.message || "Không thể tải báo cáo thu & công nợ");
    } finally {
      setLoading(false);
    }
  }, [
    filters.from_date,
    filters.to_date,
    filters.branch_id,
    filters.payer_type,
    filters.direction,
    filters.group,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExportExcel = async () => {
    if (!data) {
      showErrorToast("Không có dữ liệu để xuất");
      return;
    }
    setExporting(true);
    try {
      await exportCollectionsExcel(data);
      showSuccessToast("Đã xuất Excel");
    } catch (err) {
      showErrorToast(err?.message || "Xuất Excel thất bại");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) {
      showErrorToast("Không có dữ liệu để xuất");
      return;
    }
    setExporting(true);
    try {
      await exportCollectionsPdf(data);
      showSuccessToast("Đã xuất PDF");
    } catch (err) {
      showErrorToast(err?.message || "Xuất PDF thất bại");
    } finally {
      setExporting(false);
    }
  };

  const kpis = useMemo(
    () =>
      calcCollectionsKpis(
        data?.summary,
        data?.unpaid_invoices || [],
        data?.receipts || []
      ),
    [data]
  );

  const overdueCount = (data?.unpaid_invoices || []).filter(
    (r) => Number(r.days_outstanding) >= 30
  ).length;

  const filterKey = `${filters.from_date}|${filters.to_date}|${filters.branch_id}|${filters.direction}|${filters.group}|${filters.payer_type}`;
  const receiptsPage = useReportTablePage(
    data?.receipts || [],
    `${filterKey}|r${data?.receipts?.length || 0}`
  );
  const unpaidPage = useReportTablePage(
    data?.unpaid_invoices || [],
    `${filterKey}|u${data?.unpaid_invoices?.length || 0}`
  );

  return (
    <div className="space-y-3 sm:space-y-5 p-3 md:p-0 pb-4">
      <ReportPageHeader
        eyebrow="Báo cáo tài chính"
        title="Thu tiền & công nợ"
        description="Collection rate, DSO proxy và aging AR — nắm nhanh khả năng thu hồi và công nợ treo."
        scopeLabel={data?.scope?.scopeLabel}
        fromDate={filters.from_date}
        toDate={filters.to_date}
        extraBadges={[
          <Badge
            key="period"
            tone="red"
            className="!border-white/20 !bg-white/10 !text-white/90"
          >
            {periodLabel(filters)}
          </Badge>,
          ...(overdueCount > 0
            ? [
                <Badge
                  key="overdue"
                  tone="red"
                  className="!border-white/20 !bg-amber-400/90 !text-amber-950"
                >
                  {overdueCount} HĐ ≥ 30 ngày
                </Badge>,
              ]
            : []),
        ]}
      />

      <FinanceReportFilter
        filters={filters}
        onChange={setFilters}
        onRefresh={load}
        onExportExcel={handleExportExcel}
        onExportPdf={handleExportPdf}
        exporting={exporting}
        loading={loading}
        canPickBranch={!!data?.scope?.canPickBranch}
        branchOptions={data?.branch_options || []}
        showPayerType
      />

      <SummaryStatGrid>
        <SummaryStatCard
          label="Đã thu (Cash in)"
          value={money(kpis.receiptTotal)}
          hint={`${data?.summary?.receipt_count || 0} phiếu thu`}
          icon={Receipt}
          tone="emerald"
          badge={<Badge tone="emerald">Collected</Badge>}
        />
        <SummaryStatCard
          label="Công nợ AR"
          value={money(kpis.unpaidTotal)}
          hint={`${data?.summary?.unpaid_count || 0} hóa đơn treo`}
          icon={FileWarning}
          tone="amber"
          badge={<Badge tone="amber">Receivable</Badge>}
        />
        <SummaryStatCard
          label="Collection rate"
          value={`${kpis.collectionRate.toFixed(1)}%`}
          hint="Thu / (Thu + Công nợ)"
          icon={Target}
          tone={kpis.collectionRate >= 70 ? "emerald" : "amber"}
          badge={
            <Badge tone={kpis.collectionRate >= 70 ? "emerald" : "amber"}>
              {kpis.collectionRate >= 70 ? "Tốt" : "Cần đẩy thu"}
            </Badge>
          }
        />
        <SummaryStatCard
          label="DSO proxy"
          value={`${kpis.dsoProxy.toFixed(0)} ngày`}
          hint="Ngày treo TB có trọng số theo giá trị"
          icon={Clock3}
          tone={kpis.dsoProxy >= 30 ? "red" : "blue"}
          badge={
            <Badge tone={kpis.dsoProxy >= 30 ? "red" : "blue"}>
              {kpis.dsoProxy >= 30 ? "Chậm thu" : "Ổn"}
            </Badge>
          }
        />
        <SummaryStatCard
          label="Quá hạn ≥ 30 ngày"
          value={money(kpis.overdue30)}
          hint={`${kpis.overdueShare.toFixed(0)}% tổng công nợ`}
          icon={AlertTriangle}
          tone={kpis.overdue30 > 0 ? "red" : "emerald"}
          badge={
            <Badge tone={kpis.overdue30 > 0 ? "red" : "emerald"}>
              Aging risk
            </Badge>
          }
        />
        <SummaryStatCard
          label="Giá trị phiếu TB"
          value={money(kpis.avgReceipt)}
          hint="Ticket size phiếu thu trong kỳ"
          icon={Percent}
          tone="blue"
          badge={<Badge tone="blue">AOV</Badge>}
        />
      </SummaryStatGrid>

      <CollectionsCharts data={data} filters={filters} loading={loading} />

      <SectionCard
        title="Thu theo nguồn"
        countLabel={`${data?.receipt_summary?.length || 0} nguồn`}
      >
        <div className="grid grid-cols-2 gap-2.5 p-3 sm:gap-3 sm:p-4 sm:grid-cols-2 lg:grid-cols-4">
          {!data?.receipt_summary?.length ? (
            <div className="col-span-full">
              <EmptyState message="Chưa có phiếu thu trong kỳ" />
            </div>
          ) : (
            data.receipt_summary.map((row) => (
              <div
                key={row.payer_type}
                className="rounded-xl sm:rounded-2xl border border-gray-100 bg-gradient-to-br from-gray-50/80 to-white p-3 sm:p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <PayerTypeBadge
                    payerType={row.payer_type}
                    label={row.payer_type_label}
                  />
                  <Badge tone="slate">{row.count} phiếu</Badge>
                </div>
                <div className="mt-2.5 sm:mt-3 text-base sm:text-xl font-black text-emerald-700 tabular-nums break-all">
                  {money(row.total)}
                </div>
                <div className="mt-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Tổng thu nguồn này
                </div>
              </div>
            ))
          )}
        </div>
      </SectionCard>

      <SectionCard
        title="Danh sách phiếu thu"
        countLabel={`${data?.receipts?.length || 0} dòng`}
      >
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-400">Đang tải...</div>
        ) : !data?.receipts?.length ? (
          <EmptyState message="Không có phiếu thu" />
        ) : (
          <>
            <MobileReportCardList>
              {receiptsPage.pageRows.map((row) => (
                <MobileReportCard
                  key={`rcard-${row.id}`}
                  title={row.receipt_code}
                  subtitle={row.payer_name || "—"}
                  amount={money(row.amount)}
                  amountTone="text-emerald-700"
                  badges={
                    <PayerTypeBadge
                      payerType={row.payer_type}
                      label={row.payer_type_label}
                    />
                  }
                  meta={`${dateVN(row.receipt_date)}${
                    row.branch_name ? ` · ${row.branch_name}` : ""
                  }`}
                />
              ))}
            </MobileReportCardList>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left">
                <thead className="border-b border-gray-100 bg-gray-50/60">
                  <tr>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Ngày
                    </th>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Mã
                    </th>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Nguồn
                    </th>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Người nộp
                    </th>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Chi nhánh
                    </th>
                    <th className="p-4 text-right text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Số tiền
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {receiptsPage.pageRows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50/70">
                      <td className="p-4 text-sm font-medium text-gray-700">
                        {dateVN(row.receipt_date)}
                      </td>
                      <td className="p-4">
                        <span className="rounded-lg bg-emerald-50 px-2 py-1 font-mono text-xs font-bold text-emerald-800">
                          {row.receipt_code}
                        </span>
                      </td>
                      <td className="p-4">
                        <PayerTypeBadge
                          payerType={row.payer_type}
                          label={row.payer_type_label}
                        />
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {row.payer_name || "—"}
                      </td>
                      <td className="p-4">
                        {row.branch_name ? (
                          <Badge tone="slate">{row.branch_name}</Badge>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-4 text-right text-sm font-black text-emerald-700">
                        {money(row.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <ReportTablePagination
          show={receiptsPage.showPagination}
          total={receiptsPage.total}
          currentPage={receiptsPage.currentPage}
          pageSize={receiptsPage.pageSize}
          onPageChange={receiptsPage.setCurrentPage}
          onPageSizeChange={receiptsPage.setPageSize}
        />
      </SectionCard>

      <SectionCard
        title="Hóa đơn bán đã xuất chưa thu"
        countLabel={`${data?.unpaid_invoices?.length || 0} dòng`}
        action={
          overdueCount > 0 ? (
            <Badge tone="red">{overdueCount} quá hạn ≥ 30 ngày</Badge>
          ) : (
            <Badge tone="emerald">Không quá hạn nặng</Badge>
          )
        }
      >
        {!data?.unpaid_invoices?.length ? (
          <EmptyState message="Không có hóa đơn chưa thu trong kỳ" />
        ) : (
          <>
            <MobileReportCardList>
              {unpaidPage.pageRows.map((row) => (
                <MobileReportCard
                  key={`ucard-${row.id}`}
                  title={row.invoice_number}
                  subtitle={row.partner_name}
                  amount={money(row.total_amount)}
                  amountTone="text-amber-700"
                  badges={
                    <>
                      <InvoiceStatusBadge
                        status={row.processing_status}
                        label={row.processing_status_label}
                      />
                      <DaysOutstandingBadge days={row.days_outstanding} />
                    </>
                  }
                  meta={`${dateVN(row.invoice_date)}${
                    row.branch_name ? ` · ${row.branch_name}` : ""
                  }`}
                />
              ))}
            </MobileReportCardList>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left">
                <thead className="border-b border-gray-100 bg-gray-50/60">
                  <tr>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Ngày HĐ
                    </th>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Số HĐ
                    </th>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Đối tác
                    </th>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Chi nhánh
                    </th>
                    <th className="p-4 text-right text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Số tiền
                    </th>
                    <th className="p-4 text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Trạng thái
                    </th>
                    <th className="p-4 text-center text-[11px] font-black uppercase tracking-widest text-gray-400">
                      Ngày treo
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {unpaidPage.pageRows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50/70">
                      <td className="p-4 text-sm font-medium text-gray-700">
                        {dateVN(row.invoice_date)}
                      </td>
                      <td className="p-4">
                        <span className="rounded-lg bg-amber-50 px-2 py-1 font-mono text-xs font-bold text-amber-800">
                          {row.invoice_number}
                        </span>
                      </td>
                      <td className="p-4 text-sm font-semibold text-gray-800">
                        {row.partner_name}
                      </td>
                      <td className="p-4">
                        {row.branch_name ? (
                          <Badge tone="slate">{row.branch_name}</Badge>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-4 text-right text-sm font-black text-amber-700">
                        {money(row.total_amount)}
                      </td>
                      <td className="p-4">
                        <InvoiceStatusBadge
                          status={row.processing_status}
                          label={row.processing_status_label}
                        />
                      </td>
                      <td className="p-4 text-center">
                        <DaysOutstandingBadge days={row.days_outstanding} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <ReportTablePagination
          show={unpaidPage.showPagination}
          total={unpaidPage.total}
          currentPage={unpaidPage.currentPage}
          pageSize={unpaidPage.pageSize}
          onPageChange={unpaidPage.setCurrentPage}
          onPageSizeChange={unpaidPage.setPageSize}
        />
      </SectionCard>
    </div>
  );
}
